"use strict";

import SliderHelper from './slider-helper';
import {isset, roundf} from '../../js-utilities';
import {ManagerEvent} from './calculator-helpers';

export default class ManagerHelper {
    events = new EventTarget();

    constructor(variableName, controller, updateAgeInfluenceFunction) {
        this.variableName = variableName;
        this.controller = controller;
        this.updateAgeInfluenceFunction = updateAgeInfluenceFunction;
        this.constraint = this.controller.constraints[this.variableName];
        this.language = this.controller.language;
        this.setup();
    }

    // An extension of the constructor, this is only run one time when the class is instantiated
    setup() {
        this.sliderHelper = new SliderHelper(
            // Id of text input element, ex: waist
            this.variableName,
            // Id of slider element, ex. waist_slider
            `${this.variableName}_slider`,
            // Id of the slider clear value button
            `${this.variableName}_slider_clear`,
            // Id of hovering slider value label, ex: waist_slider_value
            `${this.variableName}_slider_value`,
            // Id of slider min value label, ex: waist_slider_lower_limit
            `${this.variableName}_slider_lower_limit`,
            // Id of slider max value label, ex: waist_slider_upper_limit
            `${this.variableName}_slider_upper_limit`,
            // Function to call when value changes
            this.update.bind(this)
        );

        // Relay an event to allow customization of the sliderValueLabel.innerHTML, though there is probably a better way to do this
        this.sliderHelper.events.addEventListener('setSliderValueLabel', (event) => {
            // Can't relay an existing event that's being dispatched
            //   and making it asynchronous breaks cancelling it
            //   so create a new event that's basically just a copy of it.
            const cancelled = !this.events.dispatchEvent(new ManagerEvent(event.type, event, event.payload));
            // Relay if it was cancelled
            if (cancelled) {
                event.preventDefault();
            }
        });

        this.configureInput();
    }

    /*
     * Setup limits, values, and language for the slider and inputs.
     * This only 'outputs' the value from the controller to the UI controls.
     * This is primarily run once on instantiation but can also be re-run
     *   if the controller values are directly updated and the UI controls need to be updated to match.
     */
    configureInput() {
        // Emit an event that configure is starting. Most things won't use this, but make it available in case anything does.
        this.events.dispatchEvent(new ManagerEvent('beforeConfigureInput'));
        const min = typeof this.constraint.min === 'function' ? this.constraint.min(this.controller.current_values) : this.constraint.min;
        const max = typeof this.constraint.max === 'function' ? this.constraint.max(this.controller.current_values) : this.constraint.max;
        const precision = typeof this.constraint.precision === 'function' ? this.constraint.precision(this.controller.current_values) : this.constraint.precision;
        const currentValue = this.controller.current_values[this.variableName];
        const imperialConversion = this.constraint.imperialConversion;
        const imperial = this.controller.current_values.imperial;
        // Convert precision (0, 1, 2) to decimal places (1, 0.1, 0.01)
        const step = 1 / (10 ** precision);

        this.sliderHelper.setRange(min, max, step);
        this.sliderHelper.setLanguage({
            // Text to use in <name>_slider_value when no value is set, ex. waist_slider_label_empty / Drag slider to set waist
            emptyLabel: this.language[`${this.variableName}_slider_label_empty`],
            // Text to use if the control is disabled, will default to emptyLabel if unset
            disabledLabel: this.language[`${this.variableName}_slider_label_disabled`],
            // Text to prefix to the value in <name>_slider_value when one is set, ex. waist_slider_label_prefix / 'Waist': <value> inches
            labelPrefix: this.language[`${this.variableName}_slider_label_prefix`],
            /*
             * Text to append to the value in <name>_slider_value when one is set, ex. waist_slider_label_suffix_imperial / Waist: <value> 'inches'
             * Check if there's an imperial conversion for the value. If not then just use a name without specificity (bmi_slider_label_suffix)
             *   however if there is an imperial conversion, append _imperial or _metric to the property name. ex. waist_slider_label_suffix_imperial
             */
            labelSuffix: this.language[`${this.variableName}_slider_label_suffix${imperialConversion ? (imperial ? '_imperial' : '_metric') : ''}`],
        });

        this.callGenerateSliderGradient();

        // If there is a value set, tell slider helper to set the UI control values, if not, tell it to clear them (slider control should be calculated to rest in the middle)
        if (isset(currentValue)) {
            const outputValue = (imperial && imperialConversion) ? (currentValue / imperialConversion) : currentValue;
            this.sliderHelper.setValue(roundf(outputValue, precision));
        } else {
            this.sliderHelper.setValue();
        }
        // Emit an event that configure is done. Most things won't use this, but make it available in case anything does.
        this.events.dispatchEvent(new ManagerEvent('afterConfigureInput'));
    }

    // Function called when the UI control value has changed and that value needs to be set into the controller.
    update(newValue, event) {
        const imperialConversion = this.constraint.imperialConversion;
        const imperial = this.controller.current_values.imperial;
        const precision = typeof this.constraint.precision === 'function' ? this.constraint.precision(this.controller.current_values) : this.constraint.precision;
        // If there is an imperial conversion for this property, and the UI is in imperial mode, convert the values to metric to store in the controller.
        const metricValue = (imperial && imperialConversion && newValue) ? (newValue * imperialConversion) : newValue;
        // Emit an event that update is starting. Most things won't use this, but make it available in case anything does.
        this.events.dispatchEvent(new ManagerEvent('beforeUpdate', {newValue, metricValue, event}));
        if (!isset(newValue) || isNaN(newValue)) {
            this.controller.current_values[this.variableName] = undefined;
        } else {
            // Rounding to 6 is arbitrary but just trying to trim off excess digits, or floating point and rounding errors
            this.controller.current_values[this.variableName] = roundf(metricValue, 6);
        }
        // Emit an event that update is done. Most things won't use this, but make it available in case anything does.
        this.events.dispatchEvent(new ManagerEvent('afterUpdate', {newValue, metricValue, event}));
        this.updateAgeInfluenceFunction();
    }

    callGenerateSliderGradient() {
        this.controller.generateSliderGradient(this.variableName, `#${this.variableName}_slider`, this.constraint);
    }

    // public method for controller to enable and disable UI controls
    enableControls(enable) {
        this.sliderHelper.enableControls(enable);
    }
}
